import { formatMessageTime, isMuteActive, t } from '@alternativamais/chat-core';

import { avisarErro } from '../../../componentes/Avisos';
import {
  CaixaMenu,
  ItemMenu,
  SeparadorMenu,
} from '../../../componentes/Flutuante';
import { useCliente, useEstado } from '../../../chat/contexto';

/** Durações de silêncio do Discord (C18), em minutos; nulo = até reativar. */
export const DURACOES_SILENCIO: Array<{
  rotulo: string;
  minutos: number | null;
}> = [
  { rotulo: 'Por 15 minutos', minutos: 15 },
  { rotulo: 'Por 1 hora', minutos: 60 },
  { rotulo: 'Por 3 horas', minutos: 180 },
  { rotulo: 'Por 8 horas', minutos: 480 },
  { rotulo: 'Por 24 horas', minutos: 1440 },
  { rotulo: 'Até eu reativar', minutos: null },
];

/** Silencia (ou reativa) um canal ou thread nas preferências da conta. */
export function useSilenciarCanal(canalId: string) {
  const cliente = useCliente();
  return async (
    mudanca:
      | { silenciar: number | null }
      | { reativar: true }
      | { nivel: 0 | 1 | 2 | 3 },
  ) => {
    const estado = cliente.store.getState();
    const canal = estado.channels[canalId];
    const guildId = canal?.guild_id ?? '@me';
    let override: Record<string, unknown>;
    if ('reativar' in mudanca) {
      override = { muted: false, mute_config: null };
    } else if ('silenciar' in mudanca) {
      const fim =
        mudanca.silenciar === null
          ? null
          : new Date(Date.now() + mudanca.silenciar * 60_000).toISOString();
      override = {
        muted: true,
        mute_config: {
          end_time: fim,
          selected_time_window:
            mudanca.silenciar === null ? -1 : mudanca.silenciar * 60,
        },
      };
    } else {
      override = { message_notifications: mudanca.nivel };
    }
    try {
      const configuracoes = await cliente.api.updateGuildSettings(guildId, {
        channel_overrides: { [canalId]: override },
      });
      cliente.store
        .getState()
        .dispatch('USER_GUILD_SETTINGS_UPDATE', configuracoes);
    } catch (erro) {
      avisarErro(erro);
    }
  };
}

/** Sino do cabeçalho: silenciar o canal e quanto avisar. */
export function MenuNotificacoes({
  canalId,
  aoFechar,
}: {
  canalId: string;
  aoFechar: () => void;
}) {
  const override = useEstado((estado) => {
    const canal = estado.channels[canalId];
    return estado.guildSettings[
      canal?.guild_id ?? '@me'
    ]?.channel_overrides.find((item) => item.channel_id === canalId);
  });
  const mudar = useSilenciarCanal(canalId);
  const silenciado =
    !!override && isMuteActive(override.muted, override.mute_config ?? null);
  const nivel = override?.message_notifications ?? 3;
  const fim = override?.mute_config?.end_time ?? null;
  const escolher = (mudanca: Parameters<typeof mudar>[0]) => {
    aoFechar();
    void mudar(mudanca);
  };
  return (
    <CaixaMenu largura={260}>
      {silenciado ? (
        <ItemMenu
          icone="sino"
          descricao={
            fim
              ? t('notificacoes.silenciadoAte', {
                  quando: formatMessageTime(fim),
                })
              : t('notificacoes.silenciado')
          }
          aoEscolher={() => escolher({ reativar: true })}
        >
          Reativar canal
        </ItemMenu>
      ) : (
        <>
          <div className="px-2 pt-1 pb-1.5 text-xs font-semibold tracking-[.04em] text-texto-3 uppercase">
            Silenciar canal
          </div>
          {DURACOES_SILENCIO.map((duracao) => (
            <ItemMenu
              key={duracao.rotulo}
              aoEscolher={() => escolher({ silenciar: duracao.minutos })}
            >
              {duracao.rotulo}
            </ItemMenu>
          ))}
        </>
      )}
      <SeparadorMenu />
      <div className="px-2 pt-1 pb-1.5 text-xs font-semibold tracking-[.04em] text-texto-3 uppercase">
        Avisar
      </div>
      {(
        [
          [3, t('notificacoes.padrao')],
          [0, t('notificacoes.todas')],
          [1, t('notificacoes.mencoes')],
          [2, t('notificacoes.nada')],
        ] as const
      ).map(([valor, rotulo]) => (
        <ItemMenu
          key={valor}
          marcado={nivel === valor}
          aoEscolher={() => escolher({ nivel: valor })}
        >
          {rotulo}
        </ItemMenu>
      ))}
    </CaixaMenu>
  );
}
